import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { HaurakiLogo } from '../components/HaurakiLogo';
import { portalApi } from '../services/portal';
import type { SignupWaitlistBlock } from '../hooks/useUserContext';
import styles from './Waitlisted.module.css';

/**
 * SIGNUP-GATE (Cluster D) · Blocked-user (waitlisted) screen.
 *
 * A person with a VALID ZewstID session whose provisioning returned
 * `403 { error:'signup_waitlisted', country, region }` lands here instead of
 * being bounced to /login (they're signed in) or dropped into an empty portal
 * (they have no learner record yet). The region gate captured them on the
 * `signup_waitlist` table at block time using their ZewstID email; this screen
 * confirms that warmly and lets them correct the email we'll reach them at.
 *
 * Layout mirrors the sign-in split (dark brand story · light action panel) so
 * the moment reads as a considered part of the founding-cohort story, not an
 * error page. Copy is real editorial, not a stack trace.
 */

type ConfirmState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  // Server accepted the (possibly corrected) email.
  | { kind: 'confirmed'; email: string }
  // The confirm call didn't land, but the gate already has them — reassure,
  // never alarm. Cardinal rule: a blocked signup is a captured lead, not a
  // dead end.
  | { kind: 'already-listed'; email: string };

/**
 * Best-effort human country name. The gate's `country` is a raw CF-IPCountry
 * ISO-3166 alpha-2 (e.g. "AU", "IN", "GB") that may be well outside our five
 * launch regions, so we resolve it through Intl.DisplayNames and fall back to
 * the bare code — never render an empty string.
 */
function countryLabel(code: string): string {
  const trimmed = (code || '').trim();
  if (!trimmed || trimmed.toUpperCase() === 'OTHER') return 'your region';
  try {
    const dn = new Intl.DisplayNames(['en'], { type: 'region' });
    return dn.of(trimmed.toUpperCase()) ?? trimmed;
  } catch {
    return trimmed;
  }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Waitlisted: React.FC<{ block: SignupWaitlistBlock }> = ({ block }) => {
  const { t } = useTranslation();
  const oidc = useAuth();
  const profile = (oidc.user?.profile ?? {}) as { email?: string; name?: string };

  const place = useMemo(() => countryLabel(block.country), [block.country]);
  const initialEmail = profile.email ?? '';

  const [email, setEmail] = useState<string>(initialEmail);
  const [state, setState] = useState<ConfirmState>({ kind: 'idle' });

  const emailValid = EMAIL_RE.test(email.trim());
  const busy = state.kind === 'saving';
  const done = state.kind === 'confirmed' || state.kind === 'already-listed';

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim();
    if (!emailValid || busy) return;
    setState({ kind: 'saving' });
    try {
      await portalApi.confirmWaitlist({
        email: value,
        detected_country: block.country,
        region: block.region,
      });
      setState({ kind: 'confirmed', email: value });
    } catch {
      // The gate already captured this signup at block time; a failed confirm
      // (endpoint not yet live, transient network) must not read as failure.
      setState({ kind: 'already-listed', email: value });
    }
  };

  const confirmedEmail =
    state.kind === 'confirmed' || state.kind === 'already-listed'
      ? state.email
      : initialEmail;

  return (
    <div className={styles.container}>
      {/* ── Left · brand story ─────────────────────────────────────────── */}
      <aside className={styles.brand}>
        <div className={styles.glow} aria-hidden="true" />

        <div className={styles.brandMark}>
          <HaurakiLogo size={20} />
          <span className={styles.brandName}>Hauraki AI</span>
        </div>

        <div className={styles.headline}>
          <p className={styles.kicker}>FOUNDING COHORT · 2026</p>
          <h1 className={styles.title}>
            We open one region at a time&mdash;on purpose.
          </h1>
          <p className={styles.subcopy}>
            {t('ako.waitlist_subcopy', { name: t('companion.name') })}
            {place !== 'your region' ? ` ${place} is next on the map.` : ''}
          </p>
        </div>

        <div className={styles.proof}>
          <div className={styles.proofItem}>
            <span className={styles.proofNum}>1:1</span>
            <span className={styles.proofLabel}>{t('ako.proof_label', { name: t('companion.name') })}</span>
          </div>
          <div className={styles.proofItem}>
            <span className={styles.proofNum}>Real</span>
            <span className={styles.proofLabel}>Graded work + capstone</span>
          </div>
        </div>
      </aside>

      {/* ── Right · action panel ───────────────────────────────────────── */}
      <main className={styles.panel}>
        <div className={styles.card} role="status" aria-live="polite">
          <div className={styles.mobileBrand}>
            <HaurakiLogo size={20} />
            <span className={styles.mobileBrandName}>Hauraki AI</span>
          </div>

          <span className={styles.badge}>
            {place === 'your region'
              ? 'On the list'
              : `${place} · opening soon`}
          </span>

          <div className={styles.heading}>
            <h2 className={styles.welcome}>
              {place === 'your region'
                ? 'You’re on the list'
                : `We’re not open in ${place} yet`}
            </h2>
            <p className={styles.sub}>
              You&rsquo;re signed in and your spot is saved. The moment we open
              enrollment {place === 'your region' ? '' : `in ${place} `}for the
              founding cohort, you&rsquo;ll be the first to hear&mdash;no need to
              check back.
            </p>
          </div>

          {done ? (
            <div className={styles.success}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
                className={styles.successIcon}
              >
                <path
                  d="M20 6 9 17l-5-5"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div>
                <p className={styles.successTitle}>You&rsquo;re confirmed.</p>
                <p className={styles.successBody}>
                  We&rsquo;ll reach you at <strong>{confirmedEmail}</strong> as
                  soon as {place === 'your region' ? 'we' : `${place}`} opens.
                </p>
              </div>
            </div>
          ) : (
            <form className={styles.form} onSubmit={handleConfirm}>
              <label className={styles.label} htmlFor="waitlist-email">
                Confirm the email we&rsquo;ll use
              </label>
              <p className={styles.labelHint}>
                We saved{' '}
                {initialEmail ? (
                  <strong>{initialEmail}</strong>
                ) : (
                  'your sign-in email'
                )}{' '}
                from your account. Prefer a different one? Change it below.
              </p>
              <input
                id="waitlist-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                className={styles.input}
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                placeholder="you@example.com"
                aria-invalid={email.length > 0 && !emailValid}
                disabled={busy}
              />
              <button
                type="submit"
                className={styles.primary}
                disabled={!emailValid || busy}
              >
                {busy ? 'Saving…' : 'Confirm my spot'}
              </button>
            </form>
          )}

          <p className={styles.foot}>
            Questions? Email{' '}
            <a className={styles.link} href="mailto:hello@hauraki.ai">
              hello@hauraki.ai
            </a>
            .
          </p>
        </div>
      </main>
    </div>
  );
};

export default Waitlisted;
